/* eslint-disable func-names */
import { useEffect, useState } from 'react';
import {
  Card, Space, Input,
} from 'antd';
import moment, { Moment } from 'moment';
import { OutlookStyleTimePicker } from './TimeRangePicker';
import DateRangePicker from './DateRangePicker';
import MultiEamilInputField from './ReceipientsField';

interface EventTimeSelectorProps {
  onTimeChange: (start: string, end: string) => void;
  selectedRange: { start: string; end: string } | null;
  setEventData: (val: string) => void;
  profileInfoEmail: string
}

const EventTimeSelector = function ({
  selectedRange, profileInfoEmail, onTimeChange, setEventData,
}: EventTimeSelectorProps) {
  const [startDate, setStartDate] = useState<Moment | null>();
  const [endDate, setEndDate] = useState<Moment | null>();
  const [emails, setEmails] = useState<string[]>([profileInfoEmail]);
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  function generateTimeOptions() {
    const options = [];
    for (let hour = 0; hour < 24; hour += 1) {
      for (let minute = 0; minute < 60; minute += 30) {
        options.push(moment().hour(hour).minute(minute).format('h:mm A'));
      }
    }
    return options;
  }

  const [timeOptions] = useState<string[]>(generateTimeOptions());
  const [subject, setSubject] = useState('');

  const handleStartDateChange = (date: Moment | null) => {
    setStartDate(date);
    setEndDate(date);
  };

  const handleEndDateChange = (date: Moment | null) => {
    setEndDate(date);
  };

  const disabledEndDate = (current: Moment) => current && current.isBefore(startDate, 'day');

  useEffect(() => {
    if (selectedRange) {
      const today = moment().format('YYYY-MM-DD');
      const newStartDate = moment(`${today} ${selectedRange.start}`);
      const newEndDate = moment(`${today} ${selectedRange.end}`);

      if (newStartDate.isValid() && newEndDate.isValid()) {
        setStartDate(newStartDate);
        setEndDate(newEndDate.hour() === 23 && newEndDate.minute() === 30
          ? newStartDate.add(1, 'day')
          : newEndDate);
      }
    }
  }, [JSON.stringify(selectedRange)]);

  useEffect(() => {
    const now = moment();
    const defaultStart = now.add(1, 'hour').startOf('hour').format('h:mm A');
    const defaultEnd = now.add(1, 'hour').startOf('hour').add(30, 'minute').format('h:mm A');

    setStartTime(defaultStart);
    setEndTime(defaultEnd);
    onTimeChange(defaultStart, defaultEnd);
  }, []);

  const handleStartTimeChange = (value: string) => {
    setStartTime(value);
    const startIndex = timeOptions.indexOf(value);
    const newEndIndex = (startIndex + 1) % timeOptions.length;
    const newEndTime = timeOptions[newEndIndex];

    setEndTime(newEndTime);
    onTimeChange(value, newEndTime);
  };

  const handleEndTimeChange = (value: string) => {
    setEndTime(value);
    onTimeChange(startTime, value);
  };

  const filteredEndTimeOptions = () => {
    if (startDate?.isSame(endDate, 'day')) {
      const startIndex = timeOptions.indexOf(startTime);
      return timeOptions.filter((_, index) => index > startIndex);
    }
    return timeOptions; // Show all options if dates are different
  };

  useEffect(() => {
    const startDateTime = moment(startDate).set('hour', moment(startTime, 'h:mm A').hour()).set('minute', moment(startTime, 'h:mm A').minute());
    const endDateTime = moment(endDate).set('hour', moment(endTime, 'h:mm A').hour()).set('minute', moment(endTime, 'h:mm A').minute());

    const attendees = emails.map((email) => ({
      emailAddress: { address: email },
    }));

    const newEmailData = {
      subject,
      start: {
        dateTime: startDateTime.format(),
        timeZone: 'Pacific Standard Time',
      },
      end: {
        dateTime: endDateTime.format(),
        timeZone: 'Pacific Standard Time',
      },
      attendees,
    };

    setEventData(JSON.stringify(newEmailData));
  }, [startDate, endDate, startTime, endTime, subject, emails, setEventData]);

  return (
    <Card style={{ width: '100%', maxWidth: 400, margin: '0 auto' }}>
      <Space direction="vertical" size="small" style={{ width: '100%' }} className="name-field">
        <div>
          <Input type="text" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Name Title" />
        </div>
        <DateRangePicker
          startDate={startDate as Moment}
          endDate={endDate as Moment}
          disabledEndDate={disabledEndDate}
          handleStartDateChange={handleStartDateChange}
          handleEndDateChange={handleEndDateChange}
        />
        <OutlookStyleTimePicker
          startTime={startTime}
          endTime={endTime}
          handleStartTimeChange={handleStartTimeChange}
          filteredEndTimeOptions={filteredEndTimeOptions}
          handleEndTimeChange={handleEndTimeChange}
        />
        <div>
          <MultiEamilInputField
            emails={emails}
            setEmails={setEmails}
          />
        </div>
      </Space>
    </Card>
  );
};

export default EventTimeSelector;
